/**
 * Direccion tributaria de la factura (CHK-04).
 *
 * Random la exige para emitir la factura (29-09-2026). Con despacho se
 * pregunta si es la misma —lo habitual— y solo si no lo es aparecen los
 * campos; con retiro en tienda no hay direccion de donde copiarla y los campos
 * estan siempre.
 *
 * La region sale de las 16 del pais y no de las de despacho: una empresa
 * puede facturar en una region a la que la tienda no despacha.
 */
import type { OpcionesDespacho } from '@elcarpintero/compartido';

import type { DatosCheckout, ErroresCheckout } from '../../utilidades/checkout';
import { pideDireccionFacturacion, regionFacturacionElegida } from '../../utilidades/checkout';
import { Campo } from './Campo';
import { SelectorRegionComuna } from './SelectorRegionComuna';

interface Props {
  datos: DatosCheckout;
  errores: ErroresCheckout;
  opciones: OpcionesDespacho | null;
  alCambiar: (cambio: Partial<DatosCheckout>) => void;
  alSalirDelCampo: (campo: keyof DatosCheckout) => void;
}

export function CamposDireccionFacturacion({
  datos,
  errores,
  opciones,
  alCambiar,
  alSalirDelCampo,
}: Props) {
  const region = regionFacturacionElegida(datos, opciones);
  const conDespacho = datos.tipoEntrega === 'despacho';

  return (
    <div className="direccion-facturacion">
      <h3>Dirección de facturación</h3>

      {conDespacho ? (
        <label className="opcion-casilla">
          <input
            type="checkbox"
            checked={datos.facturacionIgualDespacho}
            onChange={(evento) => alCambiar({ facturacionIgualDespacho: evento.target.checked })}
          />
          Es la misma dirección de despacho
        </label>
      ) : (
        <p className="campo-ayuda">
          La factura necesita la dirección de la empresa, aunque retires en la sala de ventas.
        </p>
      )}

      {pideDireccionFacturacion(datos) ? (
        <>
          <SelectorRegionComuna
            prefijoId="ck-fac"
            seccion="billing"
            regiones={opciones?.geografia ?? []}
            comunas={region?.comunas}
            codigoRegion={datos.codigoRegionFacturacion}
            comuna={datos.comunaFacturacion}
            errorRegion={errores.codigoRegionFacturacion}
            errorComuna={errores.comunaFacturacion}
            alCambiarRegion={(codigo) =>
              alCambiar({ codigoRegionFacturacion: codigo, comunaFacturacion: '' })
            }
            alCambiarComuna={(nombre) => alCambiar({ comunaFacturacion: nombre })}
            alSalirRegion={() => alSalirDelCampo('codigoRegionFacturacion')}
            alSalirComuna={() => alSalirDelCampo('comunaFacturacion')}
          />

          <div className="fila-campos">
            <Campo id="ck-fac-calle" etiqueta="Calle" error={errores.calleFacturacion}>
              {(atributos) => (
                <input
                  {...atributos}
                  type="text"
                  autoComplete="billing address-line1"
                  value={datos.calleFacturacion}
                  onChange={(evento) => alCambiar({ calleFacturacion: evento.target.value })}
                  onBlur={() => alSalirDelCampo('calleFacturacion')}
                />
              )}
            </Campo>

            <Campo id="ck-fac-numero" etiqueta="Número" error={errores.numeroFacturacion}>
              {(atributos) => (
                <input
                  {...atributos}
                  type="text"
                  value={datos.numeroFacturacion}
                  onChange={(evento) => alCambiar({ numeroFacturacion: evento.target.value })}
                  onBlur={() => alSalirDelCampo('numeroFacturacion')}
                />
              )}
            </Campo>
          </div>

          <Campo id="ck-fac-departamento" etiqueta="Departamento u oficina (opcional)">
            {(atributos) => (
              <input
                {...atributos}
                type="text"
                autoComplete="billing address-line2"
                value={datos.departamentoFacturacion}
                onChange={(evento) => alCambiar({ departamentoFacturacion: evento.target.value })}
              />
            )}
          </Campo>
        </>
      ) : null}
    </div>
  );
}
